import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';

export default function BackendConnectionProgress() {
  const location = useLocation();
  const isDashboardOrPrivate = location.pathname.startsWith('/dashboard') || 
                                location.pathname === '/scheduling' || 
                                location.pathname === '/billing';

  const [status, setStatus] = useState('connecting'); // 'connecting' | 'connected' | 'error'
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('Connecting to backend...');
  const [visible, setVisible] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());
  const abortControllerRef = useRef(null);

  // Custom analog progress calculation based on elapsed time (in ms)
  const getPacedProgress = (elapsedMs) => {
    const sec = elapsedMs / 1000;
    if (sec < 5) {
      return (sec / 5) * 30; // 0% to 30% in first 5s
    } else if (sec < 15) {
      return 30 + ((sec - 5) / 10) * 30; // 30% to 60% from 5s to 15s
    } else if (sec < 45) {
      return 60 + ((sec - 15) / 30) * 25; // 60% to 85% from 15s to 45s
    } else {
      // Asymptotically approach 98% beyond 45 seconds
      const remaining = 98 - 85;
      const extraSec = sec - 45;
      const factor = 1 - Math.exp(-extraSec / 20); // approaches 1 smoothly
      return 85 + factor * remaining;
    }
  };

  // Reset component state when navigating away from dashboard/private views
  useEffect(() => {
    if (!isDashboardOrPrivate) {
      setStatus('connecting');
      setProgress(0);
      setMessage('Connecting to backend...');
      setVisible(true);
      setRetryCount(0);
    }
  }, [isDashboardOrPrivate]);

  useEffect(() => {
    if (!isDashboardOrPrivate || status !== 'connecting') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Reset baseline start time for pacing progress
    startRef.current = Date.now();
    setProgress(0);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const currentProgress = getPacedProgress(elapsed);
      setProgress(currentProgress);

      // Maintain a clean connection message without disclosing hosting infrastructure
      setMessage('Connecting to backend...');
    }, 100);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [status, retryCount, isDashboardOrPrivate]);

  useEffect(() => {
    if (!isDashboardOrPrivate) return;
    let active = true;

    const probeBackend = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        if (!active) return;
        setStatus('connecting');

        // Ping the server. Since it has no DB lookup, it returns immediately when active
        await api.get('/api/ping', {
          signal: abortControllerRef.current.signal,
          // Low cache impact, clean request
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (!active) return;
        
        // Success flow
        setStatus('connected');
        setProgress(100);
        setMessage('Backend connection successful!');
        
        // Let user see 100% and success pill before sliding away
        setTimeout(() => {
          if (active) {
            setVisible(false);
          }
        }, 1200);

      } catch (err) {
        if (!active) return;
        if (err.name === 'CanceledError' || err.message === 'canceled') {
          return; // ignore manual aborts
        }

        console.warn('Backend connection probe failed, retrying in 3 seconds...', err);
        setStatus('error');
        setMessage(retryCount > 0 
          ? `Server connection delayed. Retrying (Attempt ${retryCount + 1})...` 
          : 'Server connection delayed. Retrying...'
        );

        // Keep progress stalled at a red state, then wait and retry
        setTimeout(() => {
          if (active) {
            setRetryCount(prev => prev + 1);
            setStatus('connecting');
          }
        }, 3000);
      }
    };

    probeBackend();

    return () => {
      active = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [retryCount, isDashboardOrPrivate]);

  if (!isDashboardOrPrivate || !visible) return null;

  // Render the progress bar and status indicator
  return (
    <>
      {/* 1. Thin horizontal strip line */}
      <div className={`fixed top-0 left-0 right-0 z-[10000] pointer-events-none h-[3px] bg-zinc-200/50 dark:bg-zinc-800/40 overflow-hidden transition-all duration-500 ${
        status === 'connected' && progress === 100 ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'
      }`}>
        <div 
          className={`h-full transition-all duration-300 ease-out ${
            status === 'connected' 
              ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' 
              : status === 'error'
                ? 'bg-rose-500'
                : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-[length:200%_auto] animate-gradient'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* 2. Floating glassmorphic pill in bottom-right corner */}
      <div className={`fixed bottom-6 right-6 z-[10000] pointer-events-auto transition-all duration-500 transform ${
        status === 'connected' && progress === 100 
          ? 'opacity-0 translate-y-4 scale-95' 
          : 'opacity-100 translate-y-0 scale-100'
      }`}>
        <div className={`px-4 py-2.5 rounded-2xl border text-xs font-semibold tracking-wide flex items-center gap-3 shadow-2xl backdrop-blur-md transition-all duration-300 ${
          status === 'connected'
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
            : status === 'error'
              ? 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400'
              : 'bg-white/85 border-zinc-200/80 text-zinc-700 dark:bg-zinc-950/85 dark:border-zinc-800/80 dark:text-zinc-300'
        }`}>
          {/* Status Indicator Icon */}
          <span className="relative flex h-2.5 w-2.5">
            {status === 'connecting' && (
              <>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
              </>
            )}
            {status === 'connected' && (
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            )}
            {status === 'error' && (
              <>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
              </>
            )}
          </span>

          {/* Connection status message */}
          <span className="font-display font-medium">{message}</span>

          {/* Additional details */}
          {status === 'connecting' && progress > 5 && (
            <span className="opacity-60 text-[10px] font-mono bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded border border-zinc-200/50 dark:border-zinc-700/50">
              {Math.round(progress)}%
            </span>
          )}
        </div>
      </div>
    </>
  );
}
